import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import LoadingState from '../components/LoadingState';
import { Truck, MessageSquare, CheckCircle, Clock, ChevronRight } from 'lucide-react';
import API from '../utils/api';

// Filmmaker-facing list of every approved film that has a delivery on file.
// Split into "needs your action" (requested / qc_failed) and "in progress"
// (submitted / qc_passed / published) so a filmmaker with several approved
// films can see at a glance which ones still need materials handed over.
// The single entry point before this was a button buried inside each film
// card on the dashboard, and the "Deliver" nav tab dead-ended on /dashboard
// whenever more than one film was awaiting delivery.

const ACTION_STATUSES = ['requested', 'qc_failed'];
const INFLIGHT_STATUSES = ['submitted', 'qc_passed', 'published'];

function metaLine(sub) {
  return [sub.genre, sub.language, sub.duration].filter(Boolean).join(' · ');
}

function ActionCard({ sub, onOpen }) {
  const isRefix = sub.delivery_status === 'qc_failed';
  return (
    <div className="card fade-up" style={{ padding: 20, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: isRefix ? 12 : 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontWeight: 800, fontSize: 17, color: 'var(--text)', letterSpacing: '-0.01em', marginBottom: 4 }}>{sub.title}</h3>
          {metaLine(sub) && <p style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>{metaLine(sub)}</p>}
        </div>
        <span className={`badge ${isRefix ? 'badge-amber' : 'badge-green'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {isRefix ? <MessageSquare size={11} /> : <Truck size={11} />}
          {isRefix ? 'Needs Fixes' : 'Delivery Requested'}
        </span>
      </div>

      {isRefix && sub.delivery_qc_notes && (
        <div style={{ background: 'rgba(255,181,71,0.06)', border: '1.5px solid rgba(255,181,71,0.25)', borderRadius: 'var(--r-md)', padding: '10px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber, #ffb547)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>QC Notes</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>{sub.delivery_qc_notes}</p>
        </div>
      )}

      <button onClick={() => onOpen(sub.id)} className="btn-primary" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {isRefix ? 'Fix and Resubmit Delivery' : 'Start Delivery'} <ChevronRight size={14} />
      </button>
    </div>
  );
}

function InflightRow({ sub }) {
  const labels = {
    submitted: 'Submitted — awaiting QC review',
    qc_passed: sub.release_on_hold
      ? 'QC passed — release on hold'
      : sub.scheduled_release_at
        ? `QC passed — going live ${new Date(sub.scheduled_release_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}`
        : 'QC passed — moving to the platform',
    published: 'Published on Shortflix',
  };
  const done = sub.delivery_status === 'published';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1.5px solid var(--border)' }}>
      <div style={{ color: done ? 'var(--green)' : 'var(--text-muted)', flexShrink: 0 }}>
        {done ? <CheckCircle size={15} /> : <Clock size={15} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{sub.title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{labels[sub.delivery_status]}</div>
      </div>
    </div>
  );
}

export default function Deliveries() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/api/submissions/my')
      .then(r => setSubs(r.data.submissions || []))
      .catch(() => setSubs([]))
      .finally(() => setLoading(false));
  }, []);

  const approved = subs.filter(s => s.status === 'approved' && s.delivery_status);
  const needsAction = approved.filter(s => ACTION_STATUSES.includes(s.delivery_status));
  const inflight = approved.filter(s => INFLIGHT_STATUSES.includes(s.delivery_status));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--pink)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>Deliveries</div>
          <h1 style={{ fontWeight: 900, fontSize: 30, color: 'var(--text)', marginBottom: 8, letterSpacing: '-0.02em' }}>Final delivery</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 400 }}>
            Hand over the final materials for each approved film so we can get it ready to publish.
          </p>
        </div>

        {loading ? (
          <LoadingState label="Loading your deliveries…" minHeight={260} />
        ) : approved.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Truck size={40} color="var(--text-muted)" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontWeight: 800, fontSize: 22, marginBottom: 8, color: 'var(--text)' }}>Nothing to deliver yet</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 14, fontWeight: 400 }}>
              When a film is approved and our team requests delivery, it'll show up here.
            </p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary">Back to My Films</button>
          </div>
        ) : (
          <>
            {needsAction.length > 0 && (
              <div style={{ marginBottom: inflight.length > 0 ? 32 : 0 }}>
                <div className="section-label" style={{ marginBottom: 12 }}>
                  Needs your action ({needsAction.length})
                </div>
                {needsAction.map(sub => (
                  <ActionCard key={sub.id} sub={sub} onOpen={id => navigate(`/deliver/${id}`)} />
                ))}
              </div>
            )}

            {needsAction.length === 0 && (
              <div style={{ background: 'rgba(0,200,150,0.06)', border: '1.5px solid rgba(0,200,150,0.2)', borderRadius: 'var(--r-md)', padding: '14px 16px', marginBottom: inflight.length > 0 ? 32 : 0, fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
                You're all caught up — no deliveries need your attention right now.
              </div>
            )}

            {inflight.length > 0 && (
              <div>
                <div className="section-label" style={{ marginBottom: 12 }}>In progress</div>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {inflight.map(sub => <InflightRow key={sub.id} sub={sub} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
